import { Phone, Bell, BellSlash, Prohibit, PencilSimple } from "@phosphor-icons/react";

/**
 * Top bar shown above the message list for the active conversation.
 * Contains the avatar + name, plus call/mute/block/edit-group action buttons.
 */
export default function TeamChatHeader({
  active,
  isActiveMuted,
  blockedUsers,
  onStartCall,
  onToggleMute,
  onToggleBlock,
  onEditGroup,
  activeTab = "chat",
  onTabChange,
}) {
  const isGroup = active.kind === "group";
  const avatar = isGroup
    ? <div className="w-9 h-9 rounded-full bg-[#EBEBFC] text-[#5B5FC7] flex items-center justify-center font-bold text-[12px]">
        {(active.group.name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
      </div>
    : <div className="w-9 h-9 rounded-full bg-[#EBEBFC] text-[#5B5FC7] flex items-center justify-center font-bold text-[12px]">
        {(active.user?.name || "?").slice(0, 1).toUpperCase()}
      </div>;

  const tabBtn = (id, label, disabled = false, title = null) => (
    <button
      key={id}
      onClick={disabled ? undefined : () => onTabChange && onTabChange(id)}
      disabled={disabled}
      title={title || undefined}
      className={
        "px-1 py-1 text-[13px] transition-colors " +
        (activeTab === id
          ? "font-semibold text-[#5B5FC7] border-b-2 border-[#5B5FC7]"
          : disabled
            ? "text-[var(--text-muted)] cursor-not-allowed"
            : "text-[var(--text-muted)] hover:text-[var(--text-primary)] border-b-2 border-transparent")
      }
      data-testid={`team-tab-${id}`}
    >
      {label}
    </button>
  );

  return (
    <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-4 bg-white shrink-0">
      {avatar}
      <div className="min-w-0">
        <div className="font-display font-bold text-[16px] truncate leading-tight" data-testid="team-chat-active-name">
          {isGroup ? active.group.name : active.user.name}
        </div>
        <div className="text-[11px] text-[var(--text-muted)] truncate uppercase tracking-wider">
          {isGroup ? (active.group.members || []).map((m) => m.name).join(", ") : active.user.role}
        </div>
      </div>
      {/* Chat / Files / Photos tabs inline with the name (iter161).
          All three are functional as of iter161-tabs — Files/Photos
          render list/grid views of the thread's attachments, reusing
          the existing AttachmentPreviewModal (iter157) via
          AttachmentChip. */}
      <div className="flex items-center gap-5 ml-2" data-testid="team-chat-tabs">
        {tabBtn("chat", "Chat")}
        {tabBtn("files", "Files")}
        {tabBtn("photos", "Photos")}
      </div>
      <div className="flex-1" />
      <button
        onClick={onStartCall}
        className="btn-ghost p-1.5 text-[#5B5FC7] hover:text-[var(--accent)]"
        title={isGroup ? "Start group call" : "Call this teammate"}
        data-testid="team-chat-call-btn"
      >
        <Phone size={16} weight="fill" />
      </button>
      <button
        onClick={onToggleMute}
        className="btn-ghost p-1.5"
        title={isActiveMuted ? "Unmute" : "Mute"}
        data-testid="team-mute-toggle"
      >
        {isActiveMuted ? <BellSlash size={14} /> : <Bell size={14} />}
      </button>
      {!isGroup && (
        <button
          onClick={onToggleBlock}
          className="btn-ghost p-1.5"
          title={blockedUsers.has(active.id) ? "Unblock" : "Block"}
          data-testid="team-block-toggle"
        >
          <Prohibit size={14} className={blockedUsers.has(active.id) ? "text-[var(--danger)]" : ""} />
        </button>
      )}
      {isGroup && (
        <button
          onClick={onEditGroup}
          className="btn-ghost p-1.5"
          title="Edit group"
          data-testid="team-group-edit-btn"
        >
          <PencilSimple size={14} />
        </button>
      )}
    </div>
  );
}
